import { Button } from "@foreday/ui/components/button";
import { IconTile } from "@foreday/ui/components/icon-tile";
import { WarningIcon } from "@foreday/ui/components/icons";
import { Skeleton } from "@foreday/ui/components/skeleton";
import { cn } from "@foreday/ui/lib/utils";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
  CatchBoundary,
  type ErrorComponentProps,
  useMatch,
  useRouter,
} from "@tanstack/react-router";
import type * as React from "react";
import { type CSSProperties, createContext, use, useEffect } from "react";
import { ScreenHeading } from "@/client/components/screen-heading";
import { clientCopy } from "@/client/copy";
import { ApiError } from "@/client/lib/api";

const copy = clientCopy.pageFailed;
const SHELL = "/_authenticated/$slug";

// The first eight characters of the request id: short enough to read out, and the logs find a
// request by any part of its id. A fault in this tab never reached the server and has none.
function errorIdOf(error: unknown): string | null {
  return error instanceof ApiError && error.requestId
    ? error.requestId.slice(0, 8)
    : null;
}

// Trying again re-runs the loaders; `reset` alone would only clear the boundary.
export function PageFailed({ error }: ErrorComponentProps) {
  const router = useRouter();
  const queries = useQueryErrorResetBoundary();
  const errorId = errorIdOf(error);

  useEffect(() => queries.reset(), [queries]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 pt-12 pb-36 text-center">
      <IconTile hue="red" size="state">
        <WarningIcon />
      </IconTile>
      <ScreenHeading className="mt-4 text-subheading tracking-tight text-foreground">
        {copy.title}
      </ScreenHeading>
      {errorId && (
        <p className="mt-2 font-mono text-body text-muted-foreground">
          {copy.errorId(errorId)}
        </p>
      )}
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <Button onClick={() => void router.invalidate()}>
          {copy.tryAgain}
        </Button>
        {errorId && (
          <Button
            variant="secondary"
            // A refused clipboard leaves the id on screen to copy by hand.
            onClick={() =>
              void navigator.clipboard.writeText(errorId).catch(() => undefined)
            }
          >
            {copy.copyId}
          </Button>
        )}
      </div>
    </div>
  );
}

const Section = createContext({ message: "", focusTo: "" });

/**
 * One part of a panel read under Suspense: a failed read is said in its place and the rest of the
 * panel stays. Try again reads it again under its own skeleton, focus waiting on `focusTo`.
 */
export function SectionBoundary({
  resetKey,
  message,
  focusTo,
  children,
}: {
  readonly resetKey: string;
  readonly message: string;
  readonly focusTo: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Section value={{ message, focusTo }}>
      <CatchBoundary
        getResetKey={() => resetKey}
        errorComponent={SectionBoundaryFailed}
      >
        {children}
      </CatchBoundary>
    </Section>
  );
}

function SectionBoundaryFailed({ reset }: ErrorComponentProps) {
  const { message, focusTo } = use(Section);
  const queries = useQueryErrorResetBoundary();
  return (
    <SectionFailed
      message={message}
      onRetry={() => {
        document.getElementById(focusTo)?.focus();
        queries.reset();
        reset();
      }}
    />
  );
}

/** A part that failed to read, said in its place, with Try again. */
export function SectionFailed({
  message,
  onRetry,
  busy = false,
}: {
  readonly message: string;
  readonly onRetry: () => void;
  /** Read again in place, with no skeleton of its own to show it. */
  readonly busy?: boolean;
}) {
  return (
    <p className="flex flex-wrap items-center gap-3 text-body text-muted-foreground">
      {message}
      <Button variant="secondary" loading={busy} onClick={onRetry}>
        {copy.tryAgain}
      </Button>
    </p>
  );
}

/** A screen that failed stands as the page's main landmark, as the screen would have. */
export function ScreenFailed(props: ErrorComponentProps) {
  return (
    <main className="flex flex-1 flex-col">
      <PageFailed {...props} />
    </main>
  );
}

// Varied title widths read as a list rather than a grid; a null chip is a row with no tag.
const GROUPS = [
  [
    { title: "w-5/12", chip: "w-24" },
    { title: "w-3/12", chip: "w-24" },
  ],
  [
    { title: "w-6/12", chip: null },
    { title: "w-4/12", chip: "w-32" },
    { title: "w-5/12", chip: "w-24" },
    { title: "w-3/12", chip: "w-32" },
    { title: "w-4/12", chip: null },
  ],
] as const;

/**
 * A task list's groups and rows while it loads, for a screen that draws its own heading. Spaced as
 * `TaskGroup` and `TaskRow` are: each line is one line of type tall, so nothing moves when the
 * list lands. `lines={2}` is a list whose rows carry their project or person under the title.
 */
export function ListSkeleton({
  className,
  style,
  lines = 1,
}: {
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly lines?: 1 | 2;
}) {
  return (
    <div aria-hidden="true" className={className} style={style}>
      {GROUPS.map((rows, group) => (
        <div key={`group-${group.toString()}`}>
          <div className="px-4 pt-6 pb-2">
            <div className="flex h-lh items-center gap-2.5 text-body">
              <Skeleton className="size-[var(--tile-size)] rounded-sm" />
              <Skeleton className="h-3.5 w-20" />
            </div>
          </div>
          {rows.map((row, at) => (
            <div
              key={`row-${at.toString()}`}
              className="relative flex flex-col justify-center gap-1 px-4 py-3.5 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border"
            >
              <div className="flex h-lh items-center gap-3 text-body">
                <Skeleton className="size-[var(--check-size)] shrink-0" />
                <Skeleton className={cn("h-3.5", row.title)} />
                {row.chip && (
                  <Skeleton
                    className={cn(
                      "ml-auto h-[var(--chip-h)] shrink-0",
                      row.chip,
                    )}
                  />
                )}
                <Skeleton
                  className={cn(
                    "hidden h-3.5 w-28 shrink-0 @panel-md/main:block",
                    row.chip ? "ml-14" : "ml-auto",
                  )}
                />
              </div>
              {lines === 2 ? (
                <div className="flex h-lh items-center pl-[calc(var(--check-size)+0.75rem)] text-label">
                  <Skeleton className="h-3 w-24" />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// A first, uncached load inside the shell: a heading and a list shaped like the screen's own.
// Shown only after the router's pending delay, so a cached revisit never sees it.
export function PanelSkeleton() {
  return (
    <div
      aria-busy="true"
      className="flex flex-col px-4 pt-6 md:px-[var(--pad-panel-x)] md:pt-4"
    >
      <div aria-hidden="true">
        <Skeleton className="h-[var(--control-h-lg)] w-40 rounded-row" />
        <Skeleton className="mt-3 h-8 w-80 max-w-full" />
        <ListSkeleton className="mt-10" />
      </div>
    </div>
  );
}

/**
 * Keeps `index.html`'s icon, or on a first load into a company the shell's outline, on while a route
 * outside the shell, or the shell itself, waits: the session read, the shell's own reads, or a
 * screen's pending code. Both are drawn there from the first paint, so marking it here restarts
 * nothing.
 */
export function BootPending() {
  return <span data-boot hidden />;
}

/** The router's pending for a route with none of its own: only the shell has a panel for a list. */
export function RoutePending() {
  // By route, not params: params name the company above the shell too.
  const routeId = useMatch({
    strict: false,
    shouldThrow: false,
    select: (match): string | undefined => match.routeId,
  });
  return routeId?.startsWith(SHELL) ? <PanelSkeleton /> : <BootPending />;
}
